import { createSelector } from 'reselect';
import moment from 'moment';
import _ from 'lodash';
import {
  SIK_INSTALL_OPTION,
  PRO_INSTALL_OPTION
} from './install.js';

export const installSelector = state => state.moversInstall;
export const reduxFromState = state => state.form;

export const timeSlotsFetchingSelector = createSelector(
  installSelector,
  install => _.get(install, 'timeSlotsFetching', true)
);

export const timeSlotsSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.professionalInstallation.timeSlots', [])
);

export const serverSelectedTimeSlotSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.professionalInstallation.selectedTimeSlotId', '')
);

export const selectedTimeSlotSelector = createSelector(
  reduxFromState,
  form => _.get(form, 'install.values.selectedTimeSlot', '')
);

export const timeSlotsHasErrorSelector = createSelector(
  reduxFromState,
  form => {
    const hasError = _.has(form, 'install.syncErrors.selectedTimeSlot');
    const isTouched = _.get(form, 'install.fields.selectedTimeSlot.touched', false);

    return hasError && isTouched;
  }
);

export const timeSlotsErrorMessageSelector = createSelector(
  reduxFromState,
  form => _.get(form, 'install.syncErrors.selectedTimeSlot', 'fallback')
);

export const timeSlotSelector = createSelector(
  timeSlotsSelector,
  selectedTimeSlotSelector,
  (timeSlots, selectedTimeSlot) => _.find(timeSlots, (obj) => obj.id === selectedTimeSlot)
);

export const equipmentSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.selfInstallation.equipments', [])
);

export const selfInstallAnswerIdSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.selfInstallation.answerId', '')
);

export const proInstallAnswerIdSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.professionalInstallation.answerId', '')
);

export const sikAvailableSelector = createSelector(
  installSelector,
  install => !_.isNil(_.get(install, 'content.selfInstallation'))
);

export const proAvailableSelector = createSelector(
  installSelector,
  install => !_.isNil(_.get(install, 'content.professionalInstallation'))
);

export const onlySikAvailableSelector = createSelector(
  sikAvailableSelector,
  proAvailableSelector,
  (sikAvailable, proAvailable) => sikAvailable && !proAvailable
);

export const onlyProAvailableSelector = createSelector(
  sikAvailableSelector,
  proAvailableSelector,
  (sikAvailable, proAvailable) => !sikAvailable && proAvailable
);

export const initialOptionSelector = createSelector(
  onlySikAvailableSelector,
  onlyProAvailableSelector,
  (onlySikAvailable, onlyProAvailable) => onlySikAvailable ? SIK_INSTALL_OPTION : onlyProAvailable ? PRO_INSTALL_OPTION : null
);

export const bothOptionsAvailableSelector = createSelector(
  sikAvailableSelector,
  proAvailableSelector,
  (sikAvailable, proAvailable) => sikAvailable && proAvailable
);

export const selectedOptionSelector = createSelector(
  installSelector,
  install => _.get(install, 'selectedOption', '')
);

export const noSelectionErrorSelector = createSelector(
  reduxFromState,
  form => {
    const currentValue = _.get(form, 'install.values.selectedOption', '');
    const hasInvalidValidValue = currentValue !== SIK_INSTALL_OPTION && currentValue !== PRO_INSTALL_OPTION;
    const isTouched = _.get(form, 'install.fields.selectedOption.touched', false);

    return hasInvalidValidValue && isTouched;
  }
);

export const sikSelectedSelector = createSelector(
  selectedOptionSelector,
  selectedOption => selectedOption === SIK_INSTALL_OPTION
);

export const proSelectedSelector = createSelector(
  selectedOptionSelector,
  selectedOption => selectedOption === PRO_INSTALL_OPTION
);

export const noSelectionSelector = createSelector(
  sikSelectedSelector,
  proSelectedSelector,
  (sik, pro) => !sik && !pro
);

export const sikServerSelectedSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.selfInstallation.isSelected', false)
);

export const proServerSelectedSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.professionalInstallation.isSelected', false)
);

export const sikPriceSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.selfInstallation.price', 0)
);

export const proPriceSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.professionalInstallation.price', 0)
);

export const customerSelector = createSelector(
  installSelector,
  install => _.get(install, 'content.customer', {})
);

export const moveToAddressSelector = createSelector(
  customerSelector,
  customer => _.get(customer, 'moveToAddress', {})
);

export const moveStartDateSelector = createSelector(
  customerSelector,
  customer => _.get(customer, 'moveStartDate', null)
);

export const moveEndDateSelector = createSelector(
  customerSelector,
  customer => _.get(customer, 'moveEndDate', null)
);

export const moveDateRangeSelector = createSelector(
  moveStartDateSelector,
  moveEndDateSelector,
  (moveStartDate, moveEndDate) => ({
    min: moveStartDate,
    max: moveEndDate
  })
);

export const serviceReconnectDateSelector = createSelector(
  customerSelector,
  customer => _.get(customer, 'serviceReconnectDate', '')
);

export const serviceStopDateSelector = createSelector(
  customerSelector,
  customer => _.get(customer, 'serviceStopDate', '')
);

export const initialServiceStopDateSelector = createSelector(
  serviceStopDateSelector,
  serviceStopDate => {
    const selectedDate = moment(serviceStopDate).format('YYYY-MM-DD');
    const initialDate = selectedDate === 'Invalid date' ? '' : selectedDate;

    return initialDate;
  }
);

export const selectedServiceReconnectDateSelector = createSelector(
  reduxFromState,
  form => _.get(form, 'install.values.serviceReconnectDate', '')
);

// Index of the selectors made available to InstallPage
// since imports and proptype checks were getting hard to manage
export const installSelectors = {
  bothOptionsAvailableSelector,
  equipmentSelector,
  initialOptionSelector,
  initialServiceStopDateSelector,
  moveDateRangeSelector,
  moveEndDateSelector,
  moveStartDateSelector,
  moveToAddressSelector,
  noSelectionSelector,
  proAvailableSelector,
  onlyProAvailableSelector,
  onlySikAvailableSelector,
  proPriceSelector,
  proSelectedSelector,
  selectedTimeSlotSelector,
  noSelectionErrorSelector,
  serviceStopDateSelector,
  serviceReconnectDateSelector,
  sikAvailableSelector,
  sikPriceSelector,
  sikSelectedSelector,
  timeSlotsFetchingSelector,
  timeSlotsHasErrorSelector,
  timeSlotsErrorMessageSelector,
  timeSlotsSelector,
  timeSlotSelector,
  serverSelectedTimeSlotSelector
};



/** WEBPACK FOOTER **
 ** ./client/redux/modules/pages/Movers/install/installSelectors.js
 **/